/**
 * Modul "Preisliste": fünf strikt getrennte Kategorien ("Langwaffen",
 * "Kurzwaffen", "Schrot-Route", "Munition", "Aufsätze"), jede als eigene
 * Spalte mit eigener Artikelliste - analog zum Aufbau von
 * pages/familyInfo/FamilyInfo.tsx.
 *
 * Rechte-Modell (siehe Migration 0048, zwei Berechtigungen, Kategorie
 * "Preisliste"): "price_list.view" (Grundvoraussetzung, App.tsx) - ALLE
 * sieben regulären Rollen, ausdrücklich auch "Member" - und
 * "price_list.manage" (Artikel hinzufügen/bearbeiten/löschen) - alle
 * regulären Rollen AUSSER "Member". "Member" sieht die Preisliste also,
 * kann sie aber nicht verändern - die entsprechenden Buttons werden dafür
 * clientseitig ausgeblendet, serverseitig zusätzlich in routes/priceList.ts
 * erzwungen.
 *
 * Ein Artikel gehört fest zu der Kategorie, in der er über "+ Artikel
 * hinzufügen" angelegt wurde - die Kategorie ist beim Bearbeiten bewusst
 * KEIN änderbares Feld, damit Artikel niemals versehentlich in eine andere
 * Spalte wandern.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import type { PriceListCategory, PriceListItem } from "../../types";

const CATEGORIES: { key: PriceListCategory; label: string }[] = [
  { key: "langwaffen", label: "Langwaffen" },
  { key: "kurzwaffen", label: "Kurzwaffen" },
  { key: "schrotroute", label: "Schrot-Route" },
  { key: "munition", label: "Munition" },
  { key: "aufsaetze", label: "Aufsätze" },
];

function formatPrice(price: number): string {
  return `${price.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

export default function PriceListPage() {
  const canManage = usePermission("price_list.manage");
  const [items, setItems] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ category: PriceListCategory; item: PriceListItem | null } | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await api.get<PriceListItem[]>("/api/price-list"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(item: PriceListItem) {
    if (!confirm(`Möchtest du "${item.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/price-list/${item.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Preisliste</h1>
        <p className="text-sm text-gray-500 mt-1">Zentrale Preisliste, unterteilt nach Kategorie.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6 items-start">
        {CATEGORIES.map((cat) => {
          const catItems = items.filter((i) => i.category === cat.key);
          return (
            <Card key={cat.key}>
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-100">{cat.label}</h3>
                {canManage && (
                  <button className="btn-primary whitespace-nowrap" onClick={() => setEditing({ category: cat.key, item: null })}>
                    + Hinzufügen
                  </button>
                )}
              </div>
              {loading ? <Spinner /> : catItems.length === 0 ? (
                <EmptyState message="Keine Artikel erfasst." />
              ) : (
                <div className="space-y-3">
                  {catItems.map((item) => (
                    <div key={item.id} className="bg-surface-raised rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-100 min-w-0 truncate">{item.name}</p>
                        <p className="text-sm text-accent whitespace-nowrap shrink-0">{formatPrice(item.price)}</p>
                      </div>
                      {item.description && <p className="text-sm text-gray-400 mt-1.5 whitespace-pre-wrap">{item.description}</p>}
                      {canManage && (
                        <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-surface-border/60">
                          <button className="text-accent text-xs hover:underline" onClick={() => setEditing({ category: cat.key, item })}>
                            Bearbeiten
                          </button>
                          <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(item)}>
                            Löschen
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {editing !== undefined && (
        <ItemForm
          category={editing.category}
          categoryLabel={CATEGORIES.find((c) => c.key === editing.category)!.label}
          item={editing.item}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}
    </div>
  );
}

function ItemForm({ category, categoryLabel, item, onClose, onSaved }: {
  category: PriceListCategory; categoryLabel: string; item: PriceListItem | null; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!item;
  const [name, setName] = useState(item?.name ?? "");
  const [price, setPrice] = useState(item?.price !== undefined ? String(item.price) : "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = { name, price: Number(price), description };
      if (isEdit) await api.put(`/api/price-list/${item!.id}`, payload);
      else await api.post("/api/price-list", { category, ...payload });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? `Artikel bearbeiten (${categoryLabel})` : `Artikel hinzufügen (${categoryLabel})`}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="price-list-item-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="price-list-item-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Artikelname *</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        <div>
          <label className="label">Preis *</label>
          <input className="input" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} required />
        </div>
        <div>
          <label className="label">Beschreibung / Notiz</label>
          <textarea className="input" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}
